import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type {
  AiFinding,
  AiFindingStatus,
  BulkDismissFindingsBody,
  BulkDismissFindingsResponse,
  DismissFindingBody,
  DismissFindingResponse,
  GetFindingsParams,
  GetFindingsResponse,
  MergeFindingsBody,
  MergeFindingsResponse,
  PromoteFindingBody,
  PromoteFindingResponse,
  UndismissAiFindingResponse,
} from "@aeriosiq/types";
import {
  useAuditLogAppendMutation,
  type AuditLogAppendResponse,
} from "../audit/queries.js";
import { useUndoStackStore } from "../analysis/state/undoStackStore.js";
import {
  fetchAllListPages,
  LIST_PAGE_SIZE_MAX,
} from "../analysis/list/fetchAllListPages.js";
import { OVERLAY_STALE_TIME_MS } from "../analysis/overlayQueryPolicy.js";

export const findingKeys = {
  all: ["findings"] as const,
  list: (inspectionId: string, params: GetFindingsParams) =>
    [...findingKeys.all, "list", inspectionId, params] as const,
};

/**
 * PERF-06 (audit I-6) — shared key + fetcher builder. The mount path
 * (`useFindingsQuery`) and the imperative overlay prefetch
 * (`useOverlayPrefetch`) both consume THIS builder, so the prefetched
 * query key and fetch behaviour are byte-identical to what the viewer
 * mounts — key drift = cache miss = wasted prefetch.
 */
export function buildFindingsListQuery(
  api: Pick<ReturnType<typeof useApi>, "get">,
  inspectionId: string,
  params: GetFindingsParams = {},
): {
  queryKey: ReturnType<typeof findingKeys.list>;
  queryFn: () => Promise<GetFindingsResponse>;
} {
  const url = `/data-acquisitions/${encodeURIComponent(inspectionId)}/findings`;
  return {
    queryKey: findingKeys.list(inspectionId, params),
    queryFn: async () => {
      const requested = params.pageSize ?? 0;
      // Within the contract cap → the exact single-call behaviour.
      if (requested <= LIST_PAGE_SIZE_MAX) {
        const response = await api.get<GetFindingsResponse>(url, {
          params: toQueryParams(params),
          paramsSerializer: { indexes: null }, // status[] -> repeated keys
        });
        return response.data;
      }
      // Above the cap (the panels' pageSize=2000 "give me everything" ask):
      // the BE silently clamps to 200, so page-walk and stitch — see
      // fetchAllListPages for the 2026-06-04 truncation story.
      return fetchAllListPages<AiFinding, GetFindingsResponse>(
        async (page, pageSize) => {
          const response = await api.get<GetFindingsResponse>(url, {
            params: toQueryParams({ ...params, page, pageSize }),
            paramsSerializer: { indexes: null },
          });
          return response.data;
        },
        requested,
      );
    },
  };
}

export function useFindingsQuery(
  inspectionId: string,
  params: GetFindingsParams = {},
) {
  const api = useApi();
  return useQuery<GetFindingsResponse>({
    ...buildFindingsListQuery(api, inspectionId, params),
    enabled: inspectionId.length > 0,
    placeholderData: (prev) => prev,
    // N-5 (audit 3.4, 2026-06-11) — paint-then-correct for the per-image
    // overlay queries: a prefetch-warmed copy paints instantly, and the
    // unconditional background revalidation corrects it within ~RTT.
    // Dataset-wide queries (no `imageId`) keep the defaults.
    ...(params.imageId
      ? { staleTime: OVERLAY_STALE_TIME_MS, refetchOnMount: "always" as const }
      : {}),
  });
}

function toQueryParams(p: GetFindingsParams) {
  const out: Record<string, unknown> = {};
  if (p.page !== undefined) out.page = p.page;
  if (p.pageSize !== undefined) out.pageSize = p.pageSize;
  if (p.status && p.status.length > 0) out.status = p.status;
  // 5.6.0 (R58-L-07) — additive params. `state` and legacy `status[]` are
  // mutually exclusive on the wire; when both are set the BE prefers
  // `state`. We send whichever the caller passes and let the BE adjudicate.
  if (p.cursor) out.cursor = p.cursor;
  if (p.imageId) out.imageId = p.imageId;
  if (p.state) out.state = p.state;
  // 5.8.0 (R58-L-05) — defect text search. Empty/whitespace-only is
  // dropped client-side; the BE additionally trims and treats empty as
  // omitted, so this is defence-in-depth.
  if (p.q && p.q.trim().length > 0) out.q = p.q.trim();
  // 5.8.0 (R58-L-05) — Track C.4 — defectClassIds typeahead AND-axis.
  if (p.defectClassIds && p.defectClassIds.length > 0) {
    out.defectClassIds = p.defectClassIds;
  }
  // 7.17.0 (SB6-ANA-FILT-07) — the AI Filter widget's committed
  // `aiClassFilter` JSON. Passed straight through (already serialised by
  // the store as the same value `getDataPoints` receives) so the
  // List/Tiles/viewer findings narrow identically to the data-point
  // counts. Absent/null ⇒ omitted ⇒ no narrowing, keeping the no-filter
  // request byte-identical to pre-FILT-07.
  if (p.aiClassFilter) out.aiClassFilter = p.aiClassFilter;
  return out;
}

export function statusToStatusPill(s: AiFindingStatus) {
  switch (s) {
    case "pending":
      return "open" as const;
    case "accepted":
      return "in_progress" as const;
    case "converted":
      return "closed" as const;
    case "dismissed":
      return "archived" as const;
  }
}

interface PromoteContext {
  previousLists: { key: readonly unknown[]; data: GetFindingsResponse }[];
}

export interface UsePromoteFindingResult {
  defectId: string;
  finding: AiFinding;
}

export function usePromoteFindingMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();

  return useMutation<
    PromoteFindingResponse,
    Error & { errorId?: string },
    { findingId: string; body?: PromoteFindingBody },
    PromoteContext
  >({
    mutationFn: async ({ findingId, body }) => {
      const response = await api.post<PromoteFindingResponse>(
        `/findings/${encodeURIComponent(findingId)}/promote`,
        body ?? {},
      );
      return response.data;
    },
    // Optimistic: flip the row's status to 'converted' across every cached
    // findings query for this inspection.
    onMutate: async ({ findingId }) => {
      const queryFamily = [...findingKeys.all, "list", inspectionId] as const;
      await qc.cancelQueries({ queryKey: queryFamily });

      const cached = qc.getQueriesData<GetFindingsResponse>({
        queryKey: queryFamily,
      });
      const ctx: PromoteContext = {
        previousLists: cached.map(([key, data]) => ({
          key,
          data: data ?? { items: [], total: 0, page: 1, pageSize: 50 },
        })),
      };

      for (const [key, data] of cached) {
        if (!data) continue;
        qc.setQueryData<GetFindingsResponse>(key, {
          ...data,
          items: data.items.map((f) =>
            f.id === findingId ? { ...f, status: "converted" } : f,
          ),
        });
      }
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const { key, data } of ctx.previousLists) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...findingKeys.all, "list", inspectionId],
      });
    },
  });
}

interface DismissContext {
  previousLists: { key: readonly unknown[]; data: GetFindingsResponse }[];
}

export function useDismissFindingMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();

  return useMutation<
    DismissFindingResponse,
    Error & { errorId?: string },
    { findingId: string; body?: DismissFindingBody },
    DismissContext
  >({
    mutationFn: async ({ findingId, body }) => {
      const response = await api.post<DismissFindingResponse>(
        `/findings/${encodeURIComponent(findingId)}/dismiss`,
        body ?? {},
      );
      return response.data;
    },
    onMutate: async ({ findingId }) => {
      const queryFamily = [...findingKeys.all, "list", inspectionId] as const;
      await qc.cancelQueries({ queryKey: queryFamily });

      const cached = qc.getQueriesData<GetFindingsResponse>({
        queryKey: queryFamily,
      });
      const ctx: DismissContext = {
        previousLists: cached.map(([key, data]) => ({
          key,
          data: data ?? { items: [], total: 0, page: 1, pageSize: 50 },
        })),
      };

      for (const [key, data] of cached) {
        if (!data) continue;
        qc.setQueryData<GetFindingsResponse>(key, {
          ...data,
          items: data.items.map((f) =>
            f.id === findingId ? { ...f, status: "dismissed" } : f,
          ),
        });
      }
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const { key, data } of ctx.previousLists) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...findingKeys.all, "list", inspectionId],
      });
    },
  });
}

interface MergeContext {
  previousLists: { key: readonly unknown[]; data: GetFindingsResponse }[];
}

export function useMergeFindingsMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();

  return useMutation<
    MergeFindingsResponse,
    Error & { errorId?: string },
    MergeFindingsBody,
    MergeContext
  >({
    mutationFn: async (body) => {
      const response = await api.post<MergeFindingsResponse>(
        "/findings/merge",
        body,
      );
      return response.data;
    },
    onMutate: async ({ findingIds }) => {
      const queryFamily = [...findingKeys.all, "list", inspectionId] as const;
      await qc.cancelQueries({ queryKey: queryFamily });

      const cached = qc.getQueriesData<GetFindingsResponse>({
        queryKey: queryFamily,
      });
      const ctx: MergeContext = {
        previousLists: cached.map(([key, data]) => ({
          key,
          data: data ?? { items: [], total: 0, page: 1, pageSize: 50 },
        })),
      };

      const idSet = new Set(findingIds);
      for (const [key, data] of cached) {
        if (!data) continue;
        qc.setQueryData<GetFindingsResponse>(key, {
          ...data,
          items: data.items.map((f) =>
            idSet.has(f.id) ? { ...f, status: "converted" } : f,
          ),
        });
      }
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const { key, data } of ctx.previousLists) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...findingKeys.all, "list", inspectionId],
      });
    },
  });
}

/**
 * Story 064 AC6 — inverse of {@link usePromoteFindingMutation}.
 * Deletes the promoted defect and flips the AI finding's status back
 * to `pending`. Spec: docs/backend-requirements/089-revert-finding-
 * promotion.md.
 */
export function useRevertFindingPromotionMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();

  return useMutation<
    { finding: AiFinding },
    Error & { errorId?: string },
    { findingId: string; defectId: string }
  >({
    mutationFn: async ({ findingId, defectId }) => {
      const response = await api.post<{ finding: AiFinding }>(
        `/findings/${encodeURIComponent(findingId)}/revert-promotion`,
        { defectId },
      );
      return response.data;
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...findingKeys.all, "list", inspectionId],
      });
      // Also invalidate defects since the promoted row is gone.
      void qc.invalidateQueries({
        queryKey: ["defects", "list", inspectionId],
      });
    },
  });
}

/**
 * Story 064 — UNDOABLE wrapper around {@link usePromoteFindingMutation}.
 * Pushes a `createDefectFromFinding` entry onto the per-image undo
 * stack and appends an audit-log row.
 */
export interface UndoablePromoteVars {
  findingId: string;
  body?: PromoteFindingBody;
  imageId: string;
  dataAcquisitionId: string;
}

export function useUndoablePromoteFindingMutation(inspectionId: string) {
  const base = usePromoteFindingMutation(inspectionId);
  const audit = useAuditLogAppendMutation();

  return useMutation<
    PromoteFindingResponse,
    Error & { errorId?: string },
    UndoablePromoteVars
  >({
    mutationFn: async (vars) => {
      const promoteVars: { findingId: string; body?: PromoteFindingBody } = {
        findingId: vars.findingId,
      };
      if (vars.body !== undefined) promoteVars.body = vars.body;
      const data = await base.mutateAsync(promoteVars);
      let auditResult: AuditLogAppendResponse | null = null;
      try {
        auditResult = await audit.mutateAsync({
          entityType: "defect",
          entityId: data.defectId,
          dataAcquisitionId: vars.dataAcquisitionId,
          action: "promoteFinding",
          before: null,
          after: { findingId: vars.findingId },
        });
      } catch (e: unknown) {
        console.warn("[064] audit-log append failed (degraded)", e);
      }
      const auditLogId =
        auditResult?.auditLogId ??
        `local-${Math.random().toString(36).slice(2)}`;
      useUndoStackStore.getState().push({
        kind: "createDefectFromFinding",
        imageId: vars.imageId,
        defectId: data.defectId,
        findingId: vars.findingId,
        auditLogId,
        description: "convert AI finding to defect",
      });
      return data;
    },
  });
}

/**
 * TRIAGE-02 (audit 3.2 / IMP-02) — UNDOABLE wrapper around
 * {@link useDismissFindingMutation}. Single dismiss is now an instant,
 * no-confirmation action (Q3 decided 2026-06-11): the optimistic cache
 * flip comes from the base mutation; this wrapper appends the audit row
 * and pushes a `dismissFinding` entry onto the GLOBAL undo stack so
 * Ctrl+Z (undismiss) reverses it within the session.
 */
export interface UndoableDismissVars {
  findingId: string;
  /** Optional reason (the Shift+X popover / preset chips path). */
  reason?: string | undefined;
  imageId: string;
  dataAcquisitionId: string;
}

export function useUndoableDismissFindingMutation(inspectionId: string) {
  const base = useDismissFindingMutation(inspectionId);
  const audit = useAuditLogAppendMutation();

  return useMutation<
    DismissFindingResponse,
    Error & { errorId?: string },
    UndoableDismissVars
  >({
    mutationFn: async (vars) => {
      const reason = vars.reason?.trim();
      const data = await base.mutateAsync(
        reason
          ? { findingId: vars.findingId, body: { reason } }
          : { findingId: vars.findingId },
      );
      let auditResult: AuditLogAppendResponse | null = null;
      try {
        auditResult = await audit.mutateAsync({
          entityType: "aiFinding",
          entityId: vars.findingId,
          dataAcquisitionId: vars.dataAcquisitionId,
          action: "update",
          before: { status: "pending" },
          after: { status: "dismissed", ...(reason ? { reason } : {}) },
        });
      } catch (e: unknown) {
        console.warn("[TRIAGE-02] audit-log append failed (degraded)", e);
      }
      const auditLogId =
        auditResult?.auditLogId ??
        `local-${Math.random().toString(36).slice(2)}`;
      useUndoStackStore.getState().push({
        kind: "dismissFinding",
        imageId: vars.imageId,
        findingId: vars.findingId,
        ...(reason ? { reason } : {}),
        auditLogId,
        description: "dismiss AI finding",
      });
      return data;
    },
  });
}

// =============================================================================
// SB5-C.7 / lyse@6.1.0 — bulk dismiss + undismiss AI findings.
// =============================================================================

interface BulkDismissContext {
  previousLists: { key: readonly unknown[]; data: GetFindingsResponse }[];
}

/**
 * R58-L-11 AC3 — bulk dismiss. Atomic mutation that targets
 * `POST /api/lyse/v1/ai-findings/bulk-dismiss` with `{ findingIds, reason }`.
 *
 * Optimistic update flips every targeted row's `status` to `'dismissed'`
 * across cached findings queries. Rollback restores the prior cache on
 * 4xx/5xx. On settle we invalidate the family so the BE-authoritative
 * `dismissed_at` lands.
 */
export function useBulkDismissFindingsMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();

  return useMutation<
    BulkDismissFindingsResponse,
    Error & { errorId?: string },
    BulkDismissFindingsBody,
    BulkDismissContext
  >({
    mutationFn: async (body) => {
      const response = await api.post<BulkDismissFindingsResponse>(
        "/ai-findings/bulk-dismiss",
        body,
      );
      return response.data;
    },
    onMutate: async ({ findingIds }) => {
      const queryFamily = [...findingKeys.all, "list", inspectionId] as const;
      await qc.cancelQueries({ queryKey: queryFamily });

      const cached = qc.getQueriesData<GetFindingsResponse>({
        queryKey: queryFamily,
      });
      const ctx: BulkDismissContext = {
        previousLists: cached.map(([key, data]) => ({
          key,
          data: data ?? { items: [], total: 0, page: 1, pageSize: 50 },
        })),
      };

      const idSet = new Set(findingIds);
      for (const [key, data] of cached) {
        if (!data) continue;
        qc.setQueryData<GetFindingsResponse>(key, {
          ...data,
          items: data.items.map((f) =>
            idSet.has(f.id) ? { ...f, status: "dismissed" } : f,
          ),
        });
      }
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const { key, data } of ctx.previousLists) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...findingKeys.all, "list", inspectionId],
      });
    },
  });
}

interface UndismissContext {
  previousLists: { key: readonly unknown[]; data: GetFindingsResponse }[];
}

/**
 * R58-L-11 AC5 — undismiss. Calls
 * `POST /api/lyse/v1/ai-findings/{id}/undismiss`. Flips the targeted
 * row's `status` back to `'pending'` optimistically; rolls back on
 * error (the BE returns 409 on `pending`/`converted`, 404 on unknown).
 */
export function useUndismissFindingMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();

  return useMutation<
    UndismissAiFindingResponse,
    Error & { errorId?: string },
    { findingId: string },
    UndismissContext
  >({
    mutationFn: async ({ findingId }) => {
      const response = await api.post<UndismissAiFindingResponse>(
        `/ai-findings/${encodeURIComponent(findingId)}/undismiss`,
        {},
      );
      return response.data;
    },
    onMutate: async ({ findingId }) => {
      const queryFamily = [...findingKeys.all, "list", inspectionId] as const;
      await qc.cancelQueries({ queryKey: queryFamily });

      const cached = qc.getQueriesData<GetFindingsResponse>({
        queryKey: queryFamily,
      });
      const ctx: UndismissContext = {
        previousLists: cached.map(([key, data]) => ({
          key,
          data: data ?? { items: [], total: 0, page: 1, pageSize: 50 },
        })),
      };

      for (const [key, data] of cached) {
        if (!data) continue;
        qc.setQueryData<GetFindingsResponse>(key, {
          ...data,
          items: data.items.map((f) =>
            f.id === findingId ? { ...f, status: "pending" } : f,
          ),
        });
      }
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const { key, data } of ctx.previousLists) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...findingKeys.all, "list", inspectionId],
      });
    },
  });
}
